// API
import { api } from '../configs/api/api';

// Models
import {
  ICostPerSessionCreate, ICostPerSessionDelete,
  ICostPerSessionRates, ICostPerSessionUpdate,
  ICounsellor, ICounsellorCallRates,
  ICounsellorCreate, ICounsellorDefaultRates,
  ICounsellorsFilters,
  ICounsellorsResponse, ICounsellorUpdateProfile, IFreeMinutes
} from '../interfaces/counsellors.interfaces';

export const CounsellorsService = {
  fetchCounsellors: async (
    filters: ICounsellorsFilters
  ): Promise<{ data?: ICounsellorsResponse, error?: string }> => {
    try {
      const params = {
        ...filters,
        offset: filters.offset ? filters.offset : 0,
        limit: filters.limit ? filters.limit : 10,
      };
      const axiosResponse = await api.get('/company/workers', {
        params
      });
      return { data: axiosResponse.data };
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  createNewCounsellor: async (
    counsellor: ICounsellorCreate
  ): Promise<{ experts?: ICounsellor, error?: string }> => {
    try {
      const axiosResponse = await api.post('/company/workers/create-worker', counsellor);
      return axiosResponse.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  activateCounsellor: async (
    worker_id: number
  ): Promise<{ worker?: ICounsellor, error?: string }> => {
    try {
      const axiosResponse = await api.post('/company/workers/activate', { worker_id });
      return axiosResponse.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  deactivateCounsellor: async (
    worker_id: number
  ): Promise<{ worker?: ICounsellor, error?: string }> => {
    try {
      const axiosResponse = await api.post('/company/workers/deactivate', { worker_id });
      return axiosResponse.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  deleteCounsellor: async (
    worker_id: number
  ): Promise<{ worker?: ICounsellor, error?: string }> => {
    try {
      const axiosResponse = await api.post('/company/workers/archive', { worker_id });
      return axiosResponse.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  blockCounsellor: async (
    worker_id: number
  ): Promise<{ worker?: ICounsellor, error?: string }> => {
    try {
      const axiosResponse = await api.post('/company/workers/block', { worker_id });
      return axiosResponse.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  unblockCounsellor: async (
    worker_id: number
  ): Promise<{ worker?: ICounsellor, error?: string }> => {
    try {
      const axiosResponse = await api.post('/company/workers/unblock', { worker_id });
      return axiosResponse.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  shareLinkCounsellor: async (
    worker_id: number
  ): Promise<{ message?: string, error?: string }> => {
    try {
      const axiosResponse = await api.post('/company/workers/send-link', { worker_id });
      return axiosResponse.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  resendInvitationCounsellor: async (
    worker_id: number
  ): Promise<{ message?: string, error?: string }> => {
    try {
      const axiosResponse = await api.post('/company/workers/resend-invitation', { worker_id });
      return axiosResponse.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  archiveCounsellor: async (
    worker_id: number
  ): Promise<{ expert?: ICounsellor, error?: string }> => {
    try {
      const axiosResponse = await api.post('/company/workers/archive', { worker_id });
      return axiosResponse.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  fetchCounsellorProfile: async (
    worker_id: number | string
  ): Promise<{ worker_profile?: ICounsellor, error?: string }> => {
    try {
      const axiosResponse = await api.get('/company/workers/profile', { params: { worker_id } });
      return axiosResponse.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  getCostPerSessionRates: async (
    worker_id: number | string
  ): Promise<{ worker_session_rates?: ICostPerSessionRates, error?: string }> => {
    try {
      const axiosResponse = await api.get('/company/workers/session-worker-rates', { params: { worker_id } });
      return axiosResponse.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  getFreeMinutesInfo: async (
    worker_id: number | string
  ): Promise<{ free_minutes?: IFreeMinutes, error?: string }> => {
    try {
      const axiosResponse = await api.get('/company/workers/free-service-settings', { params: { worker_id } });
      return { free_minutes: axiosResponse.data };
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  updateFreeMinutesInfo: async (worker_id: number | string, free_minutes: IFreeMinutes): Promise<{
    free_minutes?: IFreeMinutes,
    error?: string
  }> => {
    try {
      const axiosResponse = await api.post('/company/workers/update-free-service-settings', {
        worker_id,
        ...free_minutes,
        free_buffer_minutes: 3,
      });
      return { free_minutes: axiosResponse.data };
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  updateProfile: async (counsellor: ICounsellorUpdateProfile, worker_id: string | number): Promise<{
    message?: string,
    worker_profile?: ICounsellor,
    error?: string
  }> => {
    try {
      const response = await api.post('/company/workers/update-profile', {
        ...counsellor,
        worker_id
      });
      return response.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  updateProfileSpecialtySpecialisations: async (specialities: number[], specializations: number[] | string[], worker_id: string | number): Promise<{
    message?: string,
    worker_profile?: ICounsellor,
    error?: string
  }> => {
    try {
      const response = await api.post('/company/workers/update-profile-speciality', {
        specialities,
        specializations,
        worker_id
      });
      return response.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  updateProfileAbout: async (about: string, worker_id: string | number): Promise<{
    message?: string,
    worker_profile?: ICounsellor,
    error?: string
  }> => {
    try {
      const response = await api.post('/company/workers/update-profile-about', {
        about,
        worker_id
      });
      return response.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  updateProfilePersonalLink: async (personal_link: string, worker_id: string | number): Promise<{
    message?: string,
    worker_profile?: ICounsellor,
    error?: string
  }> => {
    try {
      const response = await api.post('/company/workers/update-personal-link', {
        personal_link,
        worker_id
      });
      return response.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  checkAvailabilityPersonalLink: async (personal_link: string): Promise<{
    message?: string,
    worker_profile?: ICounsellor,
    error?: string
  }> => {
    try {
      const response = await api.post('/workers/check-personal-link', {
        personal_link,
      });
      return response.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  updatePricePerMessage: async (is_message_rate_enabled: boolean, worker_id: string | number, message_rate?: number | string,): Promise<{
    message?: string,
    worker_default_rates?: ICounsellorDefaultRates,
    rates_enabled_count?: number,
    error?: string
  }> => {
    try {
      const response = await api.post('/company/workers/update-worker-message-rate', {
        ...(message_rate && { message_rate }),
        is_message_rate_enabled,
        worker_id
      });
      return response.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  updatePricePerMinute: async (rates: ICounsellorCallRates): Promise<{
    message?: string,
    worker_default_rates?: ICounsellorDefaultRates,
    rates_enabled_count?: number,
    error?: string
  }> => {
    try {
      const response = await api.post('/company/workers/update-worker-call-rate', rates);
      return response.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  createCounsellorSessionRate: async (rates: ICostPerSessionCreate): Promise<{
    message?: string,
    worker_session_rates?: ICostPerSessionRates,
    rates_enabled_count?: number,
    error?: string
  }> => {
    try {
      const response = await api.post('/company/workers/create-worker-session-rates', rates);
      return response.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  updateCounsellorSessionRate: async (rates: ICostPerSessionUpdate): Promise<{
    message?: string,
    worker_session_rates?: ICostPerSessionRates,
    rates_enabled_count?: number,
    error?: string
  }> => {
    try {
      const response = await api.post('/company/workers/update-worker-session-rates', rates);
      return response.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  deleteCounsellorSessionRate: async (rates: ICostPerSessionDelete): Promise<{
    message?: string,
    worker_session_rates?: ICostPerSessionRates,
    rates_enabled_count?: number,
    error?: string
  }> => {
    try {
      const response = await api.delete('/company/workers/delete-worker-session-rates', {
        params: rates
      });
      return response.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  deleteProfile: async (): Promise<any> => {
    try {
      return await api.delete('/users/delete-profile');
    } catch (error) {
      return error;
    }
  },
  updatePhoto: async (photo: File, worker_id: string | number): Promise<
    { message?: "Photo was uploaded successfully.", photo?: string, error?: string }
  > => {
    try {
      const response = await api.post('/company/workers/update-profile-photo', { photo, worker_id }, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  deletePhoto: async (worker_id: string | number): Promise<{ message?: string, error?: string }> => {
    try {
      return await api.delete('/company/workers/delete-profile-photo', { params: { worker_id } });
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
}
